import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { extractNumericId } from '../../lib/userIdExtractor';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface UnderpaidPayment {
  id: string;
  amount: number;
  shares_to_purchase: number;
  network: string;
  transaction_hash: string | null;
  transaction_notes: string | null;
  created_at: string;
  status: string;
}

interface UnderpaymentDetails {
  original_amount_received: number;
  balance_owed: number;
  total_expected: number;
  reason: string;
  marked_at: string;
}

interface UnderpaidPaymentsListProps {
  userId: number;
  onSubmitBalancePayment: (payment: UnderpaidPayment, underpaymentDetails: UnderpaymentDetails) => void;
  onRefresh?: () => void;
}

export const UnderpaidPaymentsList: React.FC<UnderpaidPaymentsListProps> = ({
  userId,
  onSubmitBalancePayment
}) => {
  const [payments, setPayments] = useState<UnderpaidPayment[]>([]);
  const [existingBalancePayments, setExistingBalancePayments] = useState<Map<string, { id: string; status: string }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUnderpaidPayments();
  }, [userId]);

  const loadUnderpaidPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate userId is numeric before querying
      const validUserId = extractNumericId(userId);
      if (!validUserId) {
        console.error('❌ Invalid userId for UnderpaidPaymentsList:', userId);
        setError('Invalid user ID. Please refresh the page or log out and log back in.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId: validUserId, dataTypes: ['underpaid_payments_with_balance'] })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      const data = result.data?.underpaid_payments_with_balance;
      setPayments(data?.payments || []);

      // Convert existingBalancePayments object to Map
      const balancePaymentsMap = new Map<string, { id: string; status: string }>();
      if (data?.existingBalancePayments) {
        Object.entries(data.existingBalancePayments).forEach(([paymentId, balanceInfo]: [string, any]) => {
          balancePaymentsMap.set(paymentId, balanceInfo);
        });
      }
      setExistingBalancePayments(balancePaymentsMap);
    } catch (err: any) {
      console.error('Error loading underpaid payments:', err);
      setError(err.message || 'Failed to load underpaid payments');
    } finally {
      setLoading(false);
    }
  };

  const parseUnderpaymentDetails = (notes: string | null): UnderpaymentDetails | null => {
    if (!notes) return null;
    try {
      const parsed = JSON.parse(notes);
      if (parsed.underpayment) {
        return {
          original_amount_received: parseFloat(parsed.underpayment.original_amount_received) || 0,
          balance_owed: parseFloat(parsed.underpayment.balance_owed) || 0,
          total_expected: parseFloat(parsed.underpayment.total_expected) || 0,
          reason: parsed.underpayment.reason || '',
          marked_at: parsed.underpayment.marked_at || ''
        };
      }
    } catch (e) {
      console.error('Error parsing underpayment details:', e);
    }
    return null;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSubmitBalance = (payment: UnderpaidPayment) => {
    const details = parseUnderpaymentDetails(payment.transaction_notes);
    if (!details) {
      alert('Unable to read underpayment details. Please contact support.');
      return;
    }
    
    if (window.confirm(
      `You are about to submit a balance payment of $${details.balance_owed.toFixed(2)}.\n\n` +
      `Original payment: $${details.original_amount_received.toFixed(2)}\n` +
      `Balance due: $${details.balance_owed.toFixed(2)}\n\n` +
      `Continue?`
    )) {
      onSubmitBalancePayment(payment, details);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '16px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', border: '2px solid #f59e0b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ color: '#f59e0b' }}>Loading underpaid payments...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', marginBottom: '16px' }}>
        <p style={{ color: '#ef4444', margin: 0 }}>{error}</p>
        <button onClick={loadUnderpaidPayments} style={{ marginTop: '8px', padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  if (payments.length === 0) {
    return null; // Don't show section if no underpaid payments
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ color: '#f59e0b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>⚠️</span> Action Required: Balance Payments ({payments.length})
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {payments.map(payment => {
          const details = parseUnderpaymentDetails(payment.transaction_notes);
          if (!details) return null;

          const existingBalance = existingBalancePayments.get(payment.id);
          const hasExistingBalancePayment = !!existingBalance;
          const balancePaymentStatus = existingBalance?.status;

          return (
            <div key={payment.id} style={{ background: 'rgba(245, 158, 11, 0.15)', border: '2px solid rgba(245, 158, 11, 0.5)', borderRadius: '12px', padding: '16px' }}>
              {/* Header with Alert Icon */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', background: hasExistingBalancePayment ? (balancePaymentStatus === 'approved' ? '#22c55e' : '#3b82f6') : '#f59e0b', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                  {hasExistingBalancePayment ? (balancePaymentStatus === 'approved' ? '✅' : '⏳') : '⚠️'}
                </div>
                <div>
                  <div style={{ color: hasExistingBalancePayment ? (balancePaymentStatus === 'approved' ? '#22c55e' : '#3b82f6') : '#f59e0b', fontSize: '16px', fontWeight: 'bold' }}>
                    {hasExistingBalancePayment ? (balancePaymentStatus === 'approved' ? 'Balance Payment Approved' : 'Balance Payment Pending') : 'Balance Payment Required'}
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '13px' }}>Submitted: {formatDate(payment.created_at)}</div>
                </div>
              </div>

              {/* Payment Summary */}
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#ffffff' }}>Expected Amount:</span>
                  <span style={{ color: '#ffffff', fontWeight: '600' }}>${details.total_expected.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#ffffff' }}>Amount Received:</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>${details.original_amount_received.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(245, 158, 11, 0.3)' }}>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>Balance Owed:</span>
                  <span style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '18px' }}>${details.balance_owed.toFixed(2)}</span>
                </div>
              </div>

              {/* Reason */}
              {details.reason && (
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '6px', padding: '10px', marginBottom: '12px' }}>
                  <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>Reason:</div>
                  <div style={{ color: '#ffffff', fontSize: '14px' }}>{details.reason}</div>
                </div>
              )}

              {/* Original Payment Info */}
              <div style={{ fontSize: '13px', color: '#ffffff', marginBottom: '16px' }}>
                <div>Original: {payment.network} • {payment.shares_to_purchase} shares</div>
                {payment.transaction_hash && (
                  <div style={{ fontFamily: 'monospace', marginTop: '4px' }}>
                    Tx: {payment.transaction_hash.slice(0, 20)}...
                  </div>
                )}
              </div>

              {/* Submit Balance Button or Status Message */}
              {hasExistingBalancePayment ? (
                <div style={{
                  width: '100%',
                  padding: '12px',
                  background: balancePaymentStatus === 'approved' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                  border: `1px solid ${balancePaymentStatus === 'approved' ? '#22c55e' : '#3b82f6'}`,
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: balancePaymentStatus === 'approved' ? '#22c55e' : '#3b82f6',
                  fontWeight: '600',
                  fontSize: '15px'
                }}>
                  {balancePaymentStatus === 'approved'
                    ? '✅ Balance payment has been approved'
                    : '⏳ Balance payment submitted - awaiting admin approval'}
                </div>
              ) : (
                <button
                  onClick={() => handleSubmitBalance(payment)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  💵 Submit Balance Payment (${details.balance_owed.toFixed(2)})
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnderpaidPaymentsList;

